// Product tour hook + provider. Uses react-joyride.
import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { Joyride, STATUS } from "react-joyride";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

const PRIMARY = "#2563EB";

const OWNER_STEPS = [
  {
    target: '[data-testid="nav-dashboard"]',
    content: "Votre vue d'ensemble. Tous les indicateurs clés apparaissent ici.",
    disableBeacon: true,
  },
  {
    target: '[data-testid="notifications-btn"]',
    content: "Vous serez notifié en temps réel des événements importants.",
  },
  {
    target: '[data-testid="nav-sites"]',
    content: "Créez et gérez vos chantiers, leurs équipes et leurs informations.",
  },
  {
    target: '[data-testid="nav-planning"]',
    content: "Planifiez les interventions de votre équipe semaine par semaine.",
  },
  {
    target: '[data-testid="nav-timesheets"]',
    content: "Validez les heures de vos équipes et exportez pour la paie.",
  },
  {
    target: '[data-testid="nav-memos"]',
    content: "Les alertes urgentes du terrain remontent directement ici.",
  },
  {
    target: '[data-testid="nav-users"]',
    content: "Invitez votre équipe et gérez leurs rôles.",
  },
  {
    target: '[data-testid="user-menu-trigger"]',
    content: "Retrouvez vos paramètres et votre profil ici. Le tour est terminé !",
  },
];

const WORKER_STEPS = [
  {
    target: '[data-testid="nav-dashboard"]',
    content: "Voici votre tableau de bord. Votre journée commence ici.",
    disableBeacon: true,
  },
  {
    target: '[data-testid="my-tasks-widget"]',
    content: "Les tâches du jour à cocher.",
  },
  {
    target: '[data-testid="nav-timesheets"]',
    content: "Pointez vos heures au fil de la journée.",
  },
  {
    target: '[data-testid="user-menu-trigger"]',
    content: "Votre profil et paramètres. Bonne journée !",
  },
];

const ProductTourContext = createContext({ start: () => {} });

export function useProductTour() {
  return useContext(ProductTourContext);
}

export function ProductTourProvider({ children }) {
  const { user, refreshAuth } = useAuth();
  const navigate = useNavigate();
  const [run, setRun] = useState(false);

  const steps = useMemo(() => {
    if (!user) return [];
    return user.role === "owner" || user.role === "manager" ? OWNER_STEPS : WORKER_STEPS;
  }, [user]);

  const start = useCallback(() => {
    navigate("/dashboard");
    setTimeout(() => setRun(true), 350);
  }, [navigate]);

  const handle = useCallback(
    async ({ status }) => {
      if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
        setRun(false);
        try {
          await api.post("/onboarding/product-tour/seen", { seen: true });
          await refreshAuth?.();
        } catch (_e) {
          /* noop */
        }
      }
    },
    [refreshAuth]
  );

  return (
    <ProductTourContext.Provider value={{ start }}>
      {children}
      <Joyride
        steps={steps}
        run={run}
        continuous
        showProgress
        showSkipButton
        scrollToFirstStep
        disableScrolling={false}
        spotlightClicks
        locale={{
          back: "Précédent",
          close: "Fermer",
          last: "Terminer",
          next: "Suivant",
          open: "Voir l'étape",
          skip: "Passer le tour",
        }}
        styles={{
          options: {
            primaryColor: PRIMARY,
            zIndex: 9999,
            textColor: "#111827",
          },
          tooltipContainer: { textAlign: "left" },
        }}
        callback={handle}
      />
    </ProductTourContext.Provider>
  );
}
